import { useEffect, useState, type FormEvent } from "react";

import { Alert, Button, Card, CardTitle, Input, Label, NativeSelect } from "@realroof/ui";

import { api, download } from "../api";
import { when } from "../format";
import { Table, td, th } from "../table";

interface Subscriber {
  user_id: string;
  name: string | null;
  mobile: string | null;
  email: string | null;
  role: string;
  city: string | null;
  packs: number;
  credits_total: number;
  credits_used: number;
  credits_left: number;
  credits_expire: string | null;
  premium_live: number;
  spent_paise: number;
  last_purchase: string | null;
}

interface Ledger {
  at: string;
  entry: string;
  credits: number;
  ad: string | null;
  by: string;
}

const rupees = (paise: number) => `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–");
const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString("en-CA");
const todayIso = () => new Date().toLocaleDateString("en-CA");

/** Subscribers & payments (wireframe A10): who has credits, their ledger, credits given by hand, GST report. */
export function Subscribers() {
  const [q, setQ] = useState("");
  const [credits, setCredits] = useState("");
  const [rows, setRows] = useState<Subscriber[] | null>(null);
  const [open, setOpen] = useState<Subscriber | null>(null);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const [error, setError] = useState("");

  const load = (e?: FormEvent) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (credits) params.set("credits", credits);
    api<Subscriber[]>(`/admin/subscribers?${params}`)
      .then(setRows)
      .catch((err: Error) => setError(err.message));
  };
  useEffect(() => load(), [credits]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Subscribers</h1>
      {error && <Alert tone="error">{error}</Alert>}
      <Card className="flex-row flex-wrap items-end gap-3">
        <strong className="w-full">GST report</strong>
        <Label>
          From
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Label>
        <Label>
          To
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Label>
        <Button onClick={() => download(`/admin/reports/gst.csv?from=${from}&to=${to}`, `realroof-gst-${from}-to-${to}.csv`).catch((err: Error) => setError(err.message))}>
          Download CSV
        </Button>
      </Card>

      <form className="flex flex-wrap items-end gap-3" onSubmit={load}>
        <Label>
          Search
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, mobile or email" />
        </Label>
        <Label>
          Credits
          <NativeSelect value={credits} onChange={(e) => setCredits(e.target.value)}>
            <option value="">Any</option>
            <option value="unused">Unused credits</option>
            <option value="expiring">Credits expiring in 30 days</option>
          </NativeSelect>
        </Label>
        <Button type="submit">Search</Button>
      </form>

      {open && <Detail sub={open} onClose={() => setOpen(null)} onChanged={() => load()} />}
      {rows === null && !error && <p className="text-muted-foreground">Loading…</p>}
      {rows?.length === 0 && <p className="text-muted-foreground">No subscribers match.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Subscriber</th>
              <th className={th}>Type · city</th>
              <th className={th}>Packs</th>
              <th className={th}>Credits used / left</th>
              <th className={th}>Premium live</th>
              <th className={th}>Spent</th>
              <th className={th}>Last purchase</th>
              <th className={th}>Credits expire</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.user_id}>
                <td className={td}>
                  <b>{s.name ?? "–"}</b>
                  <div className="text-xs text-muted-foreground">{[s.mobile, s.email].filter(Boolean).join(" · ")}</div>
                </td>
                <td className={td}>{[s.role.replace("_", " "), s.city].filter(Boolean).join(" · ")}</td>
                <td className={td}>{s.packs}</td>
                <td className={td}>
                  {s.credits_used} / {s.credits_left}
                </td>
                <td className={td}>{s.premium_live}</td>
                <td className={td}>{rupees(s.spent_paise)}</td>
                <td className={td}>{day(s.last_purchase)}</td>
                <td className={td}>{day(s.credits_expire)}</td>
                <td className={td}>
                  <Button size="sm" onClick={() => setOpen(s)}>
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}

function Detail({ sub, onClose, onChanged }: { sub: Subscriber; onClose: () => void; onChanged: () => void }) {
  const [ledger, setLedger] = useState<Ledger[] | null>(null);
  const [ads, setAds] = useState("1");
  const [months, setMonths] = useState("12");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const load = () =>
    api<{ ledger: Ledger[] }>(`/admin/subscribers/${sub.user_id}`)
      .then((d) => setLedger(d.ledger))
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    load();
  }, [sub.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function give(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    try {
      await api(`/admin/subscribers/${sub.user_id}/credits`, { method: "POST", body: { ads: Number(ads), months: Number(months), reason: reason.trim() } });
      setMessage({ kind: "success", text: `${ads} credit${ads === "1" ? "" : "s"} given.` });
      setReason("");
      await load();
      onChanged();
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  return (
    <Card aria-label={`Subscriber ${sub.name ?? sub.mobile}`}>
      <div className="flex items-center justify-between gap-2">
        <CardTitle>{sub.name ?? sub.mobile} · subscriber detail</CardTitle>
        <Button variant="link" onClick={onClose}>
          Close
        </Button>
      </div>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
      <form className="flex flex-wrap items-end gap-3" onSubmit={give} aria-label="Give credits">
        <Label>
          Give credits
          <Input type="number" min={1} max={50} value={ads} onChange={(e) => setAds(e.target.value)} className="w-24" />
        </Label>
        <Label>
          Valid for (months)
          <Input type="number" min={1} max={12} value={months} onChange={(e) => setMonths(e.target.value)} className="w-24" />
        </Label>
        <Label className="min-w-64 flex-1">
          Reason (kept in the audit log)
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Label>
        <Button type="submit" disabled={reason.trim().length < 5}>
          Give
        </Button>
      </form>
      <p className="text-sm text-muted-foreground">Refunds are made from Orders.</p>
      {ledger === null ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th className={th}>Date</th>
              <th className={th}>Entry</th>
              <th className={th}>Credits</th>
              <th className={th}>Ad</th>
              <th className={th}>By</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((l, i) => (
              <tr key={i}>
                <td className={`${td} whitespace-nowrap`}>{when(l.at)}</td>
                <td className={td}>{l.entry}</td>
                <td className={td}>{l.credits > 0 ? `+${l.credits}` : l.credits}</td>
                <td className={`${td} font-mono text-xs`}>{l.ad ?? "–"}</td>
                <td className={td}>{l.by}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
